/**
 * Deals list page - thin wrapper around generic CRMObjectListPage.
 */

import { MessageChatCircle, SwitchHorizontal01 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useUser } from '@/allauth_auth/hooks';
import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { BulkMoveToPipelineModal } from '@/lib/crm/components/bulk-actions';
import type { BulkAction, ListPageConfig } from '@/lib/crm/core/types';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { CRMObjectListPage, dealListConfig } from '@/lib/crm/list';
import { useChatAboutRecords } from '@/lib/crm/list/hooks/useChatAboutRecords';
import { buildPipelineScopeFilterConditions } from '@/lib/crm/list/utils/pipelineScopeFilter';
import { PipelineZeroState } from '@/lib/crm/pipeline';
import { dealService } from '@/lib/crm/services';
import { setActiveBulkActionJob } from '@/lib/localStorage/bulkActionActiveJob';
import { useViewHealthMarker } from '@/lib/view-health';
import type {
  CrmObject,
  CrmObjectFilters,
  DealCreateData,
  FilterGroup,
} from '@/types/crm';

interface BulkSelectionState {
  isSelectAllMode: boolean;
  totalCount: number;
  filterGroups?: FilterGroup[];
  excludedEntityIds?: string[];
  search?: string;
  searchProperties?: string[];
}

const EMPTY_SELECTION: BulkSelectionState = {
  isSelectAllMode: false,
  totalCount: 0,
};

export default function DealsList() {
  const user = useUser();
  const { currentOrg } = useOrg();
  const { pipelineId } = useParams<{ pipelineId?: string }>();
  useViewHealthMarker('deals-list-ready', { ready: true });

  // Deals can't be created until a pipeline (with stages) exists. usePipelines
  // here shares the same query cache PipelinePage uses, so this adds no extra
  // request on the common path.
  const { pipelines, isLoading: pipelinesLoading } = usePipelines(
    currentOrg?.id,
    'ahoy_deal'
  );

  const [moveToPipelineModalOpen, setMoveToPipelineModalOpen] = useState(false);
  const [moveToPipelineObjects, setMoveToPipelineObjects] = useState<
    CrmObject[]
  >([]);
  const [moveToPipelineSelection, setMoveToPipelineSelection] =
    useState<BulkSelectionState>(EMPTY_SELECTION);

  const handleBulkMoveToPipeline = useCallback((objects: CrmObject[]) => {
    setMoveToPipelineObjects(objects);
    setMoveToPipelineSelection({
      isSelectAllMode: false,
      totalCount: objects.length,
    });
    setMoveToPipelineModalOpen(true);
  }, []);

  const handleBulkMoveToPipelineWithFilters = useCallback(
    (
      filters: CrmObjectFilters,
      excludedEntityIds: string[],
      totalCount: number
    ) => {
      setMoveToPipelineObjects([]);
      setMoveToPipelineSelection({
        isSelectAllMode: true,
        totalCount,
        filterGroups: filters.filter_groups,
        excludedEntityIds,
        search: filters.search,
        searchProperties: filters.search_properties,
      });
      setMoveToPipelineModalOpen(true);
    },
    []
  );

  const handleMoveToPipelineClose = useCallback(() => {
    setMoveToPipelineModalOpen(false);
    setMoveToPipelineObjects([]);
    setMoveToPipelineSelection(EMPTY_SELECTION);
  }, []);

  const handleAsyncDispatched = useCallback(
    (jobId: number, totalCount: number) => {
      if (user?.id && currentOrg?.id) {
        setActiveBulkActionJob(
          user.id,
          currentOrg.id,
          jobId,
          totalCount,
          'ahoy_deal'
        );
      }
    },
    [user?.id, currentOrg?.id]
  );

  const handleSyncComplete = useCallback(() => {}, []);

  const chatAboutRecords = useChatAboutRecords();
  const isChatAboutEnabled = useFeatureFlag('chat_about_records');

  const handleCreateDeal = useCallback(
    async (orgId: number, data: Record<string, unknown>) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      await dealService.create(
        orgId,
        data as unknown as DealCreateData,
        user.id
      );
    },
    [user?.id]
  );

  const configWithBulkActions = useMemo((): ListPageConfig => {
    const bulkMoveToPipelineAction: BulkAction = {
      id: 'move-to-pipeline',
      label: 'Move to pipeline',
      icon: SwitchHorizontal01,
      variant: 'default',
      priority: 'secondary',
      onClick: handleBulkMoveToPipeline,
      onClickWithFilters: handleBulkMoveToPipelineWithFilters,
    };

    const bulkChatAction: BulkAction = {
      id: 'chat-about-records',
      label: 'Chat about these records',
      icon: MessageChatCircle,
      variant: 'default',
      priority: 'primary',
      onClick: chatAboutRecords.onClick,
      onClickWithFilters: chatAboutRecords.onClickWithFilters,
    };

    return {
      ...dealListConfig,
      bulkActions: [
        ...(dealListConfig.bulkActions ?? []),
        bulkMoveToPipelineAction,
        ...(isChatAboutEnabled ? [bulkChatAction] : []),
      ],
    };
  }, [
    handleBulkMoveToPipeline,
    handleBulkMoveToPipelineWithFilters,
    chatAboutRecords,
    isChatAboutEnabled,
  ]);

  // Inject the pipeline-scope filter downstream of configWithBulkActions when
  // mounted on the pipeline route (`pipeline/deals/:pipelineId`). Only
  // affects the TABLE view: PipelinePage receives explicit props and never
  // reads config.defaultFilterConditions, so the kanban view is unaffected.
  // Mirrors GenericCrmList's downstream-injection pattern for custom
  // pipelineable entity types.
  const config = useMemo((): ListPageConfig => {
    const pipelineScopeConditions = buildPipelineScopeFilterConditions(
      dealListConfig.stageAssociationTypeName,
      pipelineId
    );
    if (pipelineScopeConditions.length === 0) {
      return configWithBulkActions;
    }
    return {
      ...configWithBulkActions,
      defaultFilterConditions: pipelineScopeConditions,
    };
  }, [configWithBulkActions, pipelineId]);

  // No pipeline yet → guide the user to create one (and withhold the deal
  // list/board + its Add button) instead of showing an empty board.
  if (currentOrg?.id && !pipelinesLoading && pipelines.length === 0) {
    return (
      <PipelineZeroState
        orgId={currentOrg.id}
        entityTypeName='ahoy_deal'
        entityLabel='deal'
        entityPluralLabel='deals'
      />
    );
  }

  return (
    <>
      <CRMObjectListPage config={config} onCreateObject={handleCreateDeal} />

      {currentOrg?.id && (
        <BulkMoveToPipelineModal
          isOpen={moveToPipelineModalOpen}
          orgId={currentOrg.id}
          selectedObjects={moveToPipelineObjects}
          isSelectAllMode={moveToPipelineSelection.isSelectAllMode}
          totalCount={moveToPipelineSelection.totalCount}
          filterGroups={moveToPipelineSelection.filterGroups}
          excludedEntityIds={moveToPipelineSelection.excludedEntityIds}
          search={moveToPipelineSelection.search}
          searchProperties={moveToPipelineSelection.searchProperties}
          onClose={handleMoveToPipelineClose}
          onAsyncDispatched={handleAsyncDispatched}
          onSyncComplete={handleSyncComplete}
        />
      )}
    </>
  );
}
